import { MeasurementUnitEnum } from 'domains/measurementDomain';
import { Account } from 'domains/accountDomain';
import { keys, merge, pick } from 'lodash-es';
import { PageParam } from './appDomain';
import {
  CarrierTypeEnum,
  ShippingMethodNonstandardTypeEnum,
} from './shippingDomain';
import { ShippingAccountTypeEnum } from './jobconfigDomain';
import { SiteRoleEnum } from './roleDomain';
import { OrgTypeEnum } from 'features/Platform/components/OrgAdmin/OrgTypeSelectModal';

export interface Organization {
  /**
   * The unit the numbers in a write were typed in. Request-only: never stored on the org and
   * never sent back. The bleed override is the one measurement on this record, and without this
   * a 3.18mm bleed reached the API undeclared and was stored as 3.18in.
   */
  enteredUnit?: MeasurementUnitEnum;

  /** Metric twin of `bleedSize`, sent alongside so a unit switch needs no refetch. */
  bleedSizeMm?: number;
  accountingEmail: string;
  accountingLine: string;
  addressLine1: string;
  addressLine2: string;
  annualRevenues: number;
  bankAccountNumber: string;
  capabilities: number;
  category: string;
  certification: string;
  city: string;
  company: Company;
  contactName: string;
  continuity: number;
  cost: number;
  costCenter: string;
  country: string;
  /**
   * IETF locale tag, e.g. `en-US`. Decides the org's default measurement system, and will be
   * what translations key off.
   *
   * Pre-filled from `country` but its own field, because the two genuinely differ: a printer
   * registered in Ireland may work in en-GB, a US subsidiary of a German group in de-DE.
   */
  locale?: string | null;
  email: string;
  gallerySupportEmail?: string;
  excelOrigin: string;
  fax: string;
  id: number;
  integration: number;
  name: string;
  overallScore: number;
  ownership: string;
  parentId: number;
  parentName?: string;
  phone1: string;
  phone2: string;
  postalCode: string;
  quality: number;
  reliability: number;
  routingNumber: string;
  state: string;
  support: number;
  tags: string;
  totalEmployees: number;
  version: number;
  updateAt: string;
  website: string;
  yearEstablished: string;
  zoneId: string;
  resaleNumber: string;
  skuInheritSetting: OrgSkuInheritSettingEnum;
  /** Default measurement system for this org's users. Storage stays imperial either way. */
  measurementUnit?: MeasurementUnitEnum | null;
  logo: string | null;
  // balanceBuyerId: string;
  // balanceVendorId: string;
  buyerDigitalProofOptional: boolean;
  sourcerDigitalProofOptional: boolean;
  supplierDigitalProofOptional: boolean;
  digitalProofRequired: boolean;
  marketReady: boolean;
  receiveRfm: boolean;
  boxChargeEditable: boolean;
  bleedSize: number;
  overrideBleedSize: boolean;
  defaultChooseMyPaper: boolean;
  defaultCollapseAdvancedSearch: boolean;
  supplierViewJobSummary: boolean;
  sourcerViewBillingContact: boolean;
  supplierViewBillingContact: boolean;
  sourcerAdjustBillingContact: boolean;
  supplierAdjustBillingContact: boolean;
  defaultSearchWhitePaper: boolean;
  groupJobsForSupplier: boolean;
  groupJobsForSourcer: boolean;
  sourcerTableConfig: WorkspaceTableConfig;
  supplierTableConfig: WorkspaceTableConfig;
  allowSourcerToManageAllJobFiles: boolean;
  allowSupplierToManageAllJobFiles: boolean;
  invoiceSendersList: string[];
  sendJobsToRfm: boolean;
  balancePaymentMethods: BalanceSelectPaymentMethodsEnum[];
  ignoreAddressValidation: boolean;
  balanceConfig: OrgBalanceConfigDTO;
  isSupplier: boolean;
  remitSamePhysicalAddress: boolean;
  remitEmailAddress: string;
  remitPhoneNumber: string;
  bankName: string;
  swiftCode: string;
  remitPhone1: string;
  remitAddressLine1: string;
  remitAddressLine2: string;
  remitCity: string;
  remitState: string;
  remitPostalCode: string;
  remitCountry: string;
  remitPhone2: string;
  remitFax: string;
  remitZoneId: string;
  defaultInvoiceContactId?: number;
  defaultInvoiceAddressId?: number;
  isSaas?: boolean;
  orgType?: OrgTypeEnum;
  salesRep?: string;
  orderDetailPageConfig?: OrderDetailPageConfigDTO;
  hideProject?: boolean;
  shareContactOrgDefault?: boolean;
}
export interface DieLineColorConfigDTO {
  dielineDetectionEnabled: boolean;
  dielineColorHex: string;
}
export interface PmsColorDTO {
  name: string;
  colorHex: string;
}
export interface PmsColorConfigDTO {
  pmsDetectionEnabled: boolean;
  pmsColors: PmsColorDTO[];
}

export interface OrgColorsDTO {
  orgId?: number;
  dieLineColorConfig?: DieLineColorConfigDTO;
  pmsColorConfig?: PmsColorConfigDTO;
}

export interface OrgShareContactDefaultParams {
  orgId?: number;
  shareContactOrgDefault?: boolean;
}

interface OrderDetailPageConfigDTO {
  showRequestor?: boolean;
  showBillingContact?: boolean;
  showPurchaseOrder?: boolean;
  showCostCenter?: boolean;
  showAccountingLine?: boolean;
  showOracleBillingCode?: boolean;
}

interface OrgBalanceConfigDTO {
  autoConfirmedEnabled: boolean;
  autoConfirmedPaymentMethod: BalanceSelectPaymentMethodsEnum | undefined;
  autoConfirmedPaymentMethodId: string;
  paymentMethods: BalanceSelectPaymentMethodsEnum[];
  buyerId: string;
  invoiceVendorId: string;
  invoiceTerms?: string;
  defaultPaymentMethod: BalanceSelectPaymentMethodsEnum | undefined;
}

export enum BalanceSelectPaymentMethodsEnum {
  creditCard = 'creditCard',
  achDebit = 'achDebit',
  invoice = 'invoice',
  achCredit = 'achCredit',
  internal = 'internal',
}

export enum RemitSameAddressEnum {
  sameAsAddress = 'sameAsAddress',
  notSameAsAddress = 'notSameAsAddress',
}

export interface WorkspaceTableConfig {
  inProductionLabel: WorkspaceTableConfigTableLabel;
  shippingLabel: WorkspaceTableConfigTableLabel;
  billingLabel: WorkspaceTableConfigTableLabel;
  invoicingLabel: WorkspaceTableConfigTableLabel;
}

export interface WorkspaceTableConfigTableLabel {
  standardJobs: string;
  preConfiguredSku: string;
}

export enum OrgSkuInheritSettingEnum {
  AUTO = 'AUTO',
  MANUAL = 'MANUAL',
}
export interface Company {
  id: number;
  name: string;
  version: number;
}

export type PartialOrg = Partial<Organization>;

export type CreateOrgParam =
  | { companyId: number }
  | Omit<PartialOrg, 'createAt' | 'updateAt' | 'excelOrigin' | 'version'>;

export type UpdateOrgParam = Omit<
  PartialOrg,
  'createAt' | 'updateAt' | 'excelOrigin'
>;

export type UpdateOrgTypeParam = {
  id: number;
  orgType: OrgTypeEnum;
};

export interface Company {
  id: number;
  name: string;
  version: number;
}

export enum ContactLevelEnum {
  ORG = 'ORG',
  USER = 'USER',
}

export interface Contact {
  id: number;
  version: number;
  contactLevel: ContactLevelEnum;
  addresses: ContactAddress[];
  defaultAddressId: number;
  costCenter: string;
  department: string;
  company: string;
  fullName: string;
  firstName: string;
  lastName: string;
  orgId: number;
  title: string;
  createAt: string;
  updateAt: string;
  user?: Account;
  accountingLine?: string;
  email: string;
  phoneNumber: string;
  orgDefaultContact?: boolean;
  orgName?: string;
  oracleBillingCode?: string;
}
export type PartialCt = Partial<Contact>;

export const EMPTY_CONTACT: PartialCt = {
  addresses: [],
  defaultAddressId: 0,
  costCenter: '',
  accountingLine: '',
  department: '',
  firstName: '',
  lastName: '',
  orgId: 0,
  title: '',
  company: '',
  contactLevel: ContactLevelEnum.ORG,
  phoneNumber: '',
  email: '',
  oracleBillingCode: '',
};

export type CreateCtParam = Omit<
  PartialCt,
  'createAt' | 'updateAt' | 'version'
>;

export type UpdateCtParam = Omit<PartialCt, 'createAt' | 'updateAt'>;

export interface QueryCtParam {
  firstName?: string;
  lastName?: string;
}
export interface ListCtParam {
  orgId: number;
  page: PageParam;
  query?: QueryCtParam;
}
export function buildUpdateCtParam(ct: PartialCt): UpdateCtParam {
  return pick(ct, keys(EMPTY_CONTACT), 'id', 'version', 'userId');
}

export function buildCreateCtParam(ct: PartialCt): CreateCtParam {
  return pick(ct, keys(EMPTY_CONTACT), 'userId');
}

export interface ContactAddress {
  addressLine1: string;
  addressLine2: string;
  city: string;
  contactId: number;
  country: string;
  id: number;
  ignoreShippingValidation: boolean;
  phoneNumber: string;
  postalCode: string;
  state: string;
  version: number;
  createAt: string;
  updateAt: string;
  stateProvinceCode?: string;
}
export type PartialAddr = Partial<ContactAddress>;
export const EMPTY_ADDR: PartialAddr = {
  addressLine1: '',
  addressLine2: '',
  city: '',
  contactId: 0,
  country: '',
  ignoreShippingValidation: true,
  phoneNumber: '',
  postalCode: '',
  state: '',
};
export type CreateAddrParam = Omit<
  PartialAddr,
  'createAt' | 'updateAt' | 'version'
>;
export type UpdateAddrParam = Omit<PartialAddr, 'createAt' | 'updateAt'>;
export interface QueryAddrParam {
  contactId?: number;
}
export interface ListAddrParam {
  orgId: number;
  contactId: number;
  page: PageParam;
  query?: QueryAddrParam;
}
export function buildUpdateAddrParam(addr: PartialAddr): UpdateAddrParam {
  return pick(addr, keys(EMPTY_ADDR), 'id', 'version');
}

export function buildCreateAddrParam(addr: PartialAddr): CreateAddrParam {
  return pick(addr, keys(EMPTY_ADDR));
}

const mockJobs = {
  standardJobs: 'Standard Jobs',
  preConfiguredSku: 'Pre-configured Jobs',
};

export const EMPTY_ORG: PartialOrg = {
  accountingEmail: '',
  accountingLine: '',
  addressLine1: '',
  addressLine2: '',
  annualRevenues: 0,
  category: '',
  capabilities: 0,
  continuity: 0,
  certification: '',
  city: '',
  contactName: '',
  country: '',
  cost: 0,
  costCenter: '',
  email: '',
  gallerySupportEmail: '',
  fax: '',
  integration: 0,
  name: '',
  ownership: '',
  overallScore: 0,
  marketReady: false,
  receiveRfm: true,
  parentId: undefined,
  phone1: '',
  phone2: '',
  postalCode: '',
  reliability: 0,
  state: '',
  support: 0,
  tags: '',
  totalEmployees: 0,
  website: '',
  yearEstablished: '',
  zoneId: 'America/New_York',
  // Null, not IMPERIAL. This is an override of the org's locale and "not set" is the normal
  // state - defaulted to a system here, every org ever created carries an explicit choice
  // nobody made, and its locale decides nothing for anyone.
  measurementUnit: null,
  locale: null,
  resaleNumber: '',
  routingNumber: '',
  bankAccountNumber: '',
  logo: '',
  // balanceBuyerId: '',
  // balanceVendorId: '',
  buyerDigitalProofOptional: false,
  sourcerDigitalProofOptional: false,
  supplierDigitalProofOptional: false,
  digitalProofRequired: true,
  supplierViewJobSummary: false,
  sourcerViewBillingContact: false,
  supplierViewBillingContact: false,
  sourcerAdjustBillingContact: false,
  supplierAdjustBillingContact: false,
  defaultSearchWhitePaper: false,
  bleedSize: 0,
  overrideBleedSize: false,
  defaultChooseMyPaper: false,
  defaultCollapseAdvancedSearch: false,
  groupJobsForSupplier: false,
  groupJobsForSourcer: false,
  sourcerTableConfig: {
    inProductionLabel: mockJobs,
    shippingLabel: mockJobs,
    billingLabel: mockJobs,
    invoicingLabel: mockJobs,
  },
  supplierTableConfig: {
    inProductionLabel: mockJobs,
    shippingLabel: mockJobs,
    billingLabel: mockJobs,
    invoicingLabel: mockJobs,
  },
  allowSourcerToManageAllJobFiles: false,
  allowSupplierToManageAllJobFiles: false,
  invoiceSendersList: [],
  sendJobsToRfm: false,
  balancePaymentMethods: [],
  ignoreAddressValidation: false,
  balanceConfig: {
    autoConfirmedEnabled: false,
    autoConfirmedPaymentMethod: BalanceSelectPaymentMethodsEnum.achDebit,
    autoConfirmedPaymentMethodId: '',
    paymentMethods: [],
    buyerId: '',
    invoiceVendorId: '',
    invoiceTerms: undefined,
    defaultPaymentMethod: undefined,
  },
  isSupplier: false,
  remitSamePhysicalAddress: true,
  remitEmailAddress: '',
  remitPhoneNumber: '',
  bankName: '',
  swiftCode: '',
  remitPhone1: '',
  remitAddressLine1: '',
  remitAddressLine2: '',
  remitCity: '',
  remitState: '',
  remitPostalCode: '',
  remitCountry: '',
  remitPhone2: '',
  remitFax: '',
  remitZoneId: '',
  defaultInvoiceAddressId: undefined,
  defaultInvoiceContactId: undefined,
  orgType: OrgTypeEnum.REGULAR,
  salesRep: '',
  orderDetailPageConfig: {
    showRequestor: true,
    showBillingContact: true,
    showPurchaseOrder: true,
    showCostCenter: true,
    showAccountingLine: true,
  },
  hideProject: false,
};
/**
 * `enteredUnit` is picked explicitly rather than listed in {@link EMPTY_ORG}.
 *
 * That constant doubles as the blank record a create form starts from, and a unit describes a
 * particular submission rather than an empty org. Left out of the picked keys altogether,
 * though, it was stripped from every save - which is how a 3.18mm bleed override was stored as
 * 3.18in.
 */
export function buildUpdateOrgParam(org: PartialOrg): UpdateOrgParam {
  return pick(org, keys(EMPTY_ORG), 'enteredUnit', 'id', 'version');
}

export function buildUpdateOrgTypeParam(org: PartialOrg): UpdateOrgTypeParam {
  return { id: org.id!, orgType: org.orgType! };
}

export function buildCreateOrgParam(org: PartialOrg): CreateOrgParam {
  return merge(
    { companyId: org.company?.id },
    pick(org, keys(EMPTY_ORG), 'enteredUnit')
  );
}

export const isPlatformOrg = (org: PartialOrg) => {
  return org?.id === PLATFORM_ORG_ID;
};

export const isIndustryAvgOrg = (org: PartialOrg) => {
  return org?.id === INDUSTRY_AVG_ID;
};

export const isRegularOrg = (org: PartialOrg) => {
  return org?.id !== 1;
};

export const isSaasOrg = (org: PartialOrg) => {
  return org?.orgType === OrgTypeEnum.SAAS;
};

export const isCustomerOrg = (org: PartialOrg) => {
  return org?.orgType === OrgTypeEnum.CUSTOMER;
};

export const isSaasOrSaasCustomerOrg = (
  org: PartialOrg | undefined,
  orgList: Organization[] = []
) => {
  if (!org) {
    return false;
  }

  // 如果是SAAS组织
  if (org?.orgType === OrgTypeEnum.SAAS) {
    return true;
  }

  // 如果是Customer组织但是父级组织是SAAS组织（SAAS人员切换的情况）
  if (org?.orgType === OrgTypeEnum.CUSTOMER && org?.parentId) {
    const parentOrg = orgList.find((o) => o.id === org.parentId);
    return parentOrg?.orgType === OrgTypeEnum.SAAS;
  }

  return false;
};

export const resolveGallerySupportOrg = (
  org: PartialOrg | undefined,
  orgList: Organization[] = []
): Organization | undefined => {
  if (!org) {
    return undefined;
  }

  const hasContact = (candidate: PartialOrg) =>
    Boolean(candidate.gallerySupportEmail?.trim() || candidate.phone1?.trim());

  if (hasContact(org)) {
    return org as Organization;
  }

  if (org.parentId) {
    const parent = orgList.find((o) => o.id === org.parentId);
    if (parent && hasContact(parent)) {
      return parent;
    }
  }

  return undefined;
};

export const PLATFORM_ORG_ID = 1;
export const INDUSTRY_AVG_ID = 2;
export const REVSHOP_STORE_ID = 4;

export interface UserContactAddress {
  userId: number;
  orgId: number;
  orgName: string;
  email: string;
  firstName: string;
  lastName: string;
  department: string;
  title: string;
  company: string;
  addressLine1: string;
  addressLine2: string;
  phoneNumber: string;
  city: string;
  state: string;
  country: string;
  postalCode: string;
  fullName: string;
  addressLine: string;
  contactId: number;
}

export interface SaveUserDefaultContactAddressParams {
  userId: number;
  contact: Partial<Contact>;
  address: Partial<ContactAddress>;
}

export interface OrgShippingConfigDTO {
  id: number;
  orgId: number;
  shipFrom: OrgShippingAddress;
  carriers: OrgShippingCarrier[];
  createAt: string;
  updateAt: string;
  version: number;
  accountTypeDisplay: OrgShippingAccountTypeDisplay;
}

export interface OrgShippingAddress {
  addressLine1: string;
  addressLine2: string;
  city: string;
  stateProvinceCode: string;
  postalCode: string;
  countryCode: string;
  phoneNumber: string;
  name: string;
  email: string;
}

export interface OrgShippingCarrier {
  carrierType: CarrierTypeEnum;
  allowed: boolean;
  serialNumber: number;
  nonstandard?: OrgShippingCarrierNonstandard;
  ups?: OrgShippingCarrierUps;
  fedEx?: OrgShippingCarrierFedEx;
}

export interface OrgShippingCarrierNonstandard {
  nonstandardType: ShippingMethodNonstandardTypeEnum;
  optionName: string;
  hasRouteDistance: boolean;
  routeDistance?: RouteDistanceDTO;
  cost: number;
  daysInTransit: number;
  markupPct: number;
}

interface RouteDistanceDTO {
  fromMile: number;
  toMile: number;
}

export interface OrgShippingCarrierUps {
  userName: string;
  password: string;
  clientId?: string;
  clientSecret?: string;
  accountNumber: string;
  payer: UpsPayer;
  packageEnabled?: boolean;
  freightEnabled?: boolean;
  markupPct?: number;
  serviceDisplayOrder: ShippingCarrierServiceDisplayOrderEnum;
  expansionState: ExpansionStateEnum;
}

export interface UpsPayer {
  name: string;
  address: Address;
  shipperNumber: string;
}

export interface Address {
  addressLine: string;
  city: string;
  stateProvinceCode: string;
  postalCode: string;
  countryCode: string;
}

export interface OrgShippingCarrierFedEx {
  packageEnabled: boolean;
  freightEnabled: boolean;
  clientId: string;
  clientSecret: string;
  accountNumber: string;
  ltlShipperAccount: string;
  ltlBillToAccount: string;
  serviceDisplayOrder: ShippingCarrierServiceDisplayOrderEnum;
  expansionState: ExpansionStateEnum;
  markupPct: number;
  payer: FedExPayer;
}

export interface FedExPayer {
  city: string;
  postalCode: string;
  countryCode: string;
  streetLines: string[];
  residential?: boolean;
  stateOrProvinceCode: string;
}

export enum ShippingCarrierServiceDisplayOrderEnum {
  LOWEST_TO_HIGHEST_PRICE = 'LOWEST_TO_HIGHEST_PRICE',
  FASTEST_TO_LONGEST_DELIVERY_DATE = 'FASTEST_TO_LONGEST_DELIVERY_DATE',
}

export enum ExpansionStateEnum {
  OPEN = 'OPEN',
  CLOSED = 'CLOSED',
}

export interface OrgShippingAccountTypeDisplay {
  buyerDisplay: RoleDisplay;
  sourcerDisplay: RoleDisplay;
  supplierDisplay: RoleDisplay;
}

export interface RoleDisplay {
  normalizedOption: ShippingAccountTypeEnum;
  pullDownAllowed: boolean;
  pullDownOptions: ShippingAccountTypeEnum[];
}

export interface OrgCsrDTO {
  revshopCsrDisplayFor: RevshopCsrDisplayForEnum;
  orgCsrItems: OrgCsrItemDTO[];
  specialCsrItems: SpecialCsrItemDTO[];
}

export interface OrgCsrItemDTO {
  role: SiteRoleEnum;
  display: boolean;
  fieldName: string;
  contacts: UserContactAddress[];
}

export interface SpecialCsrItemDTO {
  sku?: SpecialSkuDTO;
  contacts?: UserContactAddress[];
  roles: SiteRoleEnum[];
  serialNumber?: number;
  fieldName?: string;
}

export interface SpecialSkuDTO {
  id: number;
  name: string;
}

export enum RevshopCsrDisplayForEnum {
  ALL = 'ALL',
  BUYER = 'BUYER',
  SUPPLIER_SOURCER = 'SUPPLIER_SOURCER',
  SUPPLIER = 'SUPPLIER',
  SOURCER = 'SOURCER',
  NONE = 'NONE',
}

export interface OrgBalancePaymentDTO {
  banks: OrgBalancePaymentDTOItem[];
  creditCards: OrgBalancePaymentDTOItem[];
}

export interface OrgBalancePaymentDTOItem {
  id: string;
  name: string;
}

/**
 * The locales an org can be set to.
 *
 * A list rather than free text so the values stay ones the API can read a region out of, and so
 * the same set is available to translations later. Ordered by language so the dropdown reads as
 * a list of languages rather than a list of codes.
 */
export const ORG_LOCALES: { value: string; label: string }[] = [
  { value: 'en-US', label: 'English (United States)' },
  { value: 'en-CA', label: 'English (Canada)' },
  { value: 'en-GB', label: 'English (United Kingdom)' },
  { value: 'en-IE', label: 'English (Ireland)' },
  { value: 'en-AU', label: 'English (Australia)' },
  { value: 'en-NZ', label: 'English (New Zealand)' },
  { value: 'en-IN', label: 'English (India)' },
  { value: 'en-SG', label: 'English (Singapore)' },
  { value: 'en-ZA', label: 'English (South Africa)' },
  { value: 'fr-FR', label: 'Français (France)' },
  { value: 'fr-CA', label: 'Français (Canada)' },
  { value: 'de-DE', label: 'Deutsch (Deutschland)' },
  { value: 'de-AT', label: 'Deutsch (Österreich)' },
  { value: 'de-CH', label: 'Deutsch (Schweiz)' },
  { value: 'nl-NL', label: 'Nederlands (Nederland)' },
  { value: 'nl-BE', label: 'Nederlands (België)' },
  { value: 'es-ES', label: 'Español (España)' },
  { value: 'es-MX', label: 'Español (México)' },
  { value: 'it-IT', label: 'Italiano (Italia)' },
  { value: 'pt-PT', label: 'Português (Portugal)' },
  { value: 'pt-BR', label: 'Português (Brasil)' },
  { value: 'sv-SE', label: 'Svenska (Sverige)' },
  { value: 'nb-NO', label: 'Norsk (Norge)' },
  { value: 'da-DK', label: 'Dansk (Danmark)' },
  { value: 'fi-FI', label: 'Suomi (Suomi)' },
  { value: 'pl-PL', label: 'Polski (Polska)' },
  { value: 'cs-CZ', label: 'Čeština (Česko)' },
  { value: 'ja-JP', label: '日本語 (日本)' },
  { value: 'zh-CN', label: '中文 (中国大陆)' },
  { value: 'zh-TW', label: '中文 (台灣)' },
  { value: 'zh-HK', label: '中文 (香港)' },
  { value: 'ko-KR', label: '한국어 (대한민국)' },
  { value: 'ar-AE', label: 'العربية (الإمارات)' },
];

/**
 * The locale to suggest for an org registered in a given country.
 *
 * Only a suggestion, for pre-filling the field on a form that has a country but no locale yet.
 * The two are separate fields precisely because this guess is sometimes wrong, so nothing here
 * overwrites a locale that has already been set.
 *
 * Mirrors the backfill in the PRD-498 migration; a country absent from both simply leaves the
 * locale empty, and the API falls back to reading the country directly.
 */
const LOCALE_BY_COUNTRY: Record<string, string> = {
  US: 'en-US', CA: 'en-CA', GB: 'en-GB', IE: 'en-IE', AU: 'en-AU', NZ: 'en-NZ',
  DE: 'de-DE', AT: 'de-AT', CH: 'de-CH', FR: 'fr-FR', BE: 'nl-BE', NL: 'nl-NL',
  ES: 'es-ES', MX: 'es-MX', IT: 'it-IT', PT: 'pt-PT', BR: 'pt-BR',
  SE: 'sv-SE', NO: 'nb-NO', DK: 'da-DK', FI: 'fi-FI', PL: 'pl-PL', CZ: 'cs-CZ',
  JP: 'ja-JP', CN: 'zh-CN', TW: 'zh-TW', HK: 'zh-HK', KR: 'ko-KR',
  IN: 'en-IN', SG: 'en-SG', ZA: 'en-ZA', AE: 'ar-AE',
};

export const localeForCountry = (
  country: string | null | undefined
): string | undefined =>
  country ? LOCALE_BY_COUNTRY[country.trim().toUpperCase()] : undefined;
